import { type ReactNode, useRef, useState, useEffect, useLayoutEffect, useCallback } from 'react';
import styled from 'styled-components';

type Placement = 'top' | 'bottom' | 'left' | 'right';

type TooltipProps = {
  content: ReactNode;
  placement?: Placement;
  offset?: number;
  disabled?: boolean;
  interactive?: boolean;
  children: ReactNode;
  fullWidth?: boolean;
  className?: string;
  variant?: 'light' | 'dark';
  onClick?: () => void;
};

export const Tooltip = ({
  content,
  placement = 'top',
  offset = 10,
  disabled = false,
  interactive = false,
  children,
  fullWidth = false,
  className,
  variant = 'dark',
  onClick,
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [computedPlacement, setComputedPlacement] = useState<Placement>(placement);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resolvePlacement = useCallback((): Placement => {
    const trigger = containerRef.current;
    const el = contentRef.current;
    if (!trigger || !el) {
      return placement;
    }

    const margin = 8;
    const rect = trigger.getBoundingClientRect();
    const cw = el.offsetWidth;
    const ch = el.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    // Account for a fixed header/top bar so tooltips are not hidden behind it.
    const topSafe = Math.max(margin, document.querySelector('header')?.getBoundingClientRect().bottom ?? 0);

    const fits: Record<Placement, boolean> = {
      top: rect.top - offset - ch >= topSafe,
      bottom: rect.bottom + offset + ch <= vh - margin,
      left: rect.left - offset - cw >= margin,
      right: rect.right + offset + cw <= vw - margin,
    };

    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const horizontalCenterFits = centerX - cw / 2 >= margin && centerX + cw / 2 <= vw - margin;
    const verticalCenterFits = centerY - ch / 2 >= topSafe && centerY + ch / 2 <= vh - margin;

    const order: Placement[] =
      placement === 'top'
        ? ['top', 'bottom', 'left', 'right']
        : placement === 'bottom'
          ? ['bottom', 'top', 'left', 'right']
          : placement === 'left'
            ? ['left', 'right', 'top', 'bottom']
            : ['right', 'left', 'top', 'bottom'];

    for (const p of order) {
      if ((p === 'top' || p === 'bottom') && fits[p] && horizontalCenterFits) {
        return p;
      }
      if ((p === 'left' || p === 'right') && fits[p] && verticalCenterFits) {
        return p;
      }
    }
    for (const p of order) {
      if (fits[p]) {
        return p;
      }
    }
    return placement;
  }, [placement, offset]);

  useLayoutEffect(() => {
    if (!visible) {
      return;
    }
    setComputedPlacement(resolvePlacement());

    const update = () => setComputedPlacement(resolvePlacement());
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [visible, resolvePlacement]);

  const onChangeVisibility = (visible: boolean) => {
    if (!disabled) {
      setVisible(visible);
    }
  };

  const handleTouch = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChangeVisibility(!visible);

    if (onClick) {
      onClick();
    }
  };

  const isTouchDevice = () => 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  const handleMouseLeave = (e: React.MouseEvent<HTMLDivElement>) => {
    if (
      interactive &&
      containerRef.current &&
      e.relatedTarget &&
      containerRef.current.contains(e.relatedTarget as Node)
    ) {
      return;
    }

    if (interactive) {
      // Delay hiding to allow moving to the tooltip content
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        onChangeVisibility(false);
      }, 100);
    } else {
      onChangeVisibility(false);
    }
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return (
    <TooltipContainer
      $fullWidth={fullWidth}
      ref={containerRef}
      onClick={isTouchDevice() ? handleTouch : undefined}
      onMouseEnter={() => onChangeVisibility(true)}
      onMouseLeave={handleMouseLeave}
      className={className}
    >
      {children}
      <TooltipContent
        ref={contentRef}
        isVisible={visible}
        placement={computedPlacement}
        offset={offset}
        canInteract={interactive}
        $variant={variant}
        onMouseEnter={() => {
          if (interactive) {
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
              timeoutRef.current = null;
            }
            onChangeVisibility(true);
          }
        }}
        onMouseLeave={() => {
          if (interactive) {
            onChangeVisibility(false);
          }
        }}
      >
        <TooltipArrow placement={computedPlacement} $variant={variant} />
        {content}
      </TooltipContent>
    </TooltipContainer>
  );
};

const TooltipContainer = styled.div<{ $fullWidth?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  ${({ $fullWidth }) => $fullWidth && 'width: 100%;'}
`;

const TooltipContent = styled.div.withConfig({
  shouldForwardProp: (prop) => !['isVisible', 'placement', 'offset', 'canInteract', '$variant'].includes(prop),
})<{ isVisible: boolean; placement: string; offset: number; canInteract: boolean; $variant: 'light' | 'dark' }>`
  position: absolute;
  z-index: 10;
  outline: none;
  border: 1px solid ${({ $variant, theme }) => ($variant === 'dark' ? 'transparent' : theme.colors.prototype.border)};
  border-radius: ${({ theme, $variant }) =>
    $variant === 'dark' ? theme.borderRadius.normal : theme.borderRadius.medium};
  background-color: ${({ theme, $variant }) =>
    $variant === 'dark' ? theme.colors.prototype.foreground : theme.colors.prototype.card};
  color: ${({ theme, $variant }) =>
    $variant === 'dark' ? theme.colors.prototype.background : theme.colors.prototype.foreground};
  box-shadow: ${({ theme, $variant }) =>
    $variant === 'dark' ? theme.shadow.light : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)'};
  font-size: 11px;
  font-weight: 400;
  line-height: normal;
  letter-spacing: normal;
  text-transform: none;
  text-align: center;
  ${({ $variant }) => ($variant === 'dark' ? '&& * { color: inherit; }' : '')}
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  visibility: ${({ isVisible }) => (isVisible ? 'visible' : 'hidden')};
  transition: ${({ isVisible }) => (isVisible ? 'opacity 0.3s ease-in-out' : 'none')};
  pointer-events: ${({ canInteract }) => (canInteract ? 'auto' : 'none')};

  ${({ placement, offset }) => {
    switch (placement) {
      case 'top':
        return `
          bottom: calc(100% + ${offset}px);
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'bottom':
        return `
          top: calc(100% + ${offset}px);
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'left':
        return `
          right: calc(100% + ${offset}px);
          top: 50%;
          transform: translateY(-50%);
        `;
      case 'right':
        return `
          left: calc(100% + ${offset}px);
          top: 50%;
          transform: translateY(-50%);
        `;
      default:
        return '';
    }
  }}
`;

const TooltipArrow = styled.div.withConfig({
  shouldForwardProp: (prop) => !['placement', '$variant'].includes(prop),
})<{ placement: string; $variant: 'light' | 'dark' }>`
  position: absolute;
  width: 0;
  height: 0;

  ${({ theme, placement, $variant }) => {
    const arrowColor = $variant === 'dark' ? theme.colors.prototype.foreground : theme.colors.prototype.card;
    switch (placement) {
      case 'top':
        return `
          bottom: -5px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-top: 5px solid ${arrowColor};
        `;
      case 'bottom':
        return `
          top: -5px;
          left: 50%;
          transform: translateX(-50%);
          border-left: 5px solid transparent;
          border-right: 5px solid transparent;
          border-bottom: 5px solid ${arrowColor};
        `;
      case 'left':
        return `
          right: -5px;
          top: 50%;
          transform: translateY(-50%);
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-left: 5px solid ${arrowColor};
        `;
      case 'right':
        return `
          left: -5px;
          top: 50%;
          transform: translateY(-50%);
          border-top: 5px solid transparent;
          border-bottom: 5px solid transparent;
          border-right: 5px solid ${arrowColor};
        `;
      default:
        return '';
    }
  }}
`;
